import { Stack, Typography } from '@mui/material';
import Heading from 'components/Heading';
import React, { useEffect, useRef } from 'react';
import { NumberInput } from 'react-admin';
import { useFormContext } from 'react-hook-form';
import { getInherited } from 'utils/rateSettings/SharedRateSettingsForm.utils';
import {
  getClassificationFromSource,
  updateChargeRateCents,
  updateProRateCents,
} from '../position-rate-settings-form/PositionRateSettingsForm.utils';
import { useRateCalculations } from '../position-rate-settings-form/hooks/useRateCalculations';
import { mergeDeepWith } from 'ramda';
import _ from 'lodash';

export const RateSettingsBlendedInputs = ({
  source,
  inheritedSettings,
  type,
  taxClassification,
}) => {
  const { watch, setValue } = useFormContext();
  const fullSource = `${source}.${taxClassification}`;

  const { watchedFields } = useRateCalculations({
    source: fullSource,
    inheritedSettings,
  });

  const markup = watch(`${source}.${taxClassification}.blended_rates.blended_rate_markup`);
  const formData = watch(source);
  const { level } = getClassificationFromSource(source);
  const isLocationLevel = level === 'location_position_rate_setting';

  const mergedSettings = mergeDeepWith((a, b) => a ?? b, formData, inheritedSettings);

  const applicableRate =
    watchedFields.useBlendedRate && !isLocationLevel
      ? watchedFields.blendedRateCents
      : watchedFields.chargeRateCents;

  const nestedTaxClassificationType =
    type === 'blended-rate' ? 'blended_rates' : 'non_blended_rates';

  const settingsHash = _.get(mergedSettings, `${taxClassification}.${nestedTaxClassificationType}`);
  const taxRateTypeKey = `${taxClassification}.${nestedTaxClassificationType}`;
  const title = type === 'blended-rate' ? '12h Rate' : '8h Rate';

  const isMounted = useRef(false);

  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
      return;
    }

    if (watchedFields.lockedRateType === 'lock_professional_rate') {
      updateChargeRateCents({
        setValue,
        watch,
        source: fullSource,
        baseMarginPercent: watchedFields.marginPercent,
        blendedMarkup: markup,
        inheritedSettings,
        professionalRateCents: watchedFields.proRateCents,
        overtimeConfigurationValues: {
          ...settingsHash,
          blended_rate_markup: settingsHash?.blended_rate_markup,
        },
      });
    } else {
      updateProRateCents({
        setValue,
        watch,
        source: fullSource,
        baseMarginPercent: watchedFields.marginPercent,
        inheritedSettings,
        rateCents: applicableRate,
        overtimeConfigurationValues: settingsHash,
      });
    }
  }, [
    settingsHash?.charge_overtime_multiplier,
    settingsHash?.charge_double_overtime_multiplier,
    settingsHash?.charge_max_daily_regular_hours,
    settingsHash?.charge_max_daily_overtime_hours,
    settingsHash?.payout_overtime_multiplier,
    settingsHash?.payout_double_overtime_multiplier,
    settingsHash?.payout_max_daily_regular_hours,
    settingsHash?.payout_max_daily_overtime_hours,
  ]);

  return (
    <>
      <Heading>{title}</Heading>
      <Stack sx={{ paddingLeft: 2 }}>
        <Typography variant="h6" data-testid="heading">
          Charge
        </Typography>
        <NumberInput
          source={`${source}.${taxRateTypeKey}.charge_overtime_multiplier`}
          label="Charge Overtime Multiplier"
          helperText={getInherited(
            inheritedSettings,
            `${taxRateTypeKey}.charge_overtime_multiplier`,
          )}
        />
        <NumberInput
          source={`${source}.${taxRateTypeKey}.charge_double_overtime_multiplier`}
          label="Charge Double Overtime Multiplier"
          helperText={getInherited(
            inheritedSettings,
            `${taxRateTypeKey}.charge_double_overtime_multiplier`,
          )}
        />
        <NumberInput
          source={`${source}.${taxRateTypeKey}.charge_max_daily_regular_hours`}
          label="Charge Max Daily Regular Hours"
          helperText={getInherited(
            inheritedSettings,
            `${taxRateTypeKey}.charge_max_daily_regular_hours`,
          )}
        />
        <NumberInput
          source={`${source}.${taxRateTypeKey}.charge_max_daily_overtime_hours`}
          label="Charge Max Daily Overtime Hours"
          helperText={getInherited(
            inheritedSettings,
            `${taxRateTypeKey}.charge_max_daily_overtime_hours`,
          )}
        />

        <Typography variant="h6" data-testid="heading">
          Pay
        </Typography>
        <NumberInput
          source={`${source}.${taxRateTypeKey}.payout_overtime_multiplier`}
          label="Payout Overtime Multiplier"
          helperText={getInherited(
            inheritedSettings,
            `${taxRateTypeKey}.payout_overtime_multiplier`,
          )}
        />
        <NumberInput
          source={`${source}.${taxRateTypeKey}.payout_double_overtime_multiplier`}
          label="Payout Double Overtime Multiplier"
          helperText={getInherited(
            inheritedSettings,
            `${taxRateTypeKey}.payout_double_overtime_multiplier`,
          )}
        />
        <NumberInput
          source={`${source}.${taxRateTypeKey}.payout_max_daily_regular_hours`}
          label="Payout Max Daily Regular Hours"
          helperText={getInherited(
            inheritedSettings,
            `${taxRateTypeKey}.payout_max_daily_regular_hours`,
          )}
        />
        <NumberInput
          source={`${source}.${taxRateTypeKey}.payout_max_daily_overtime_hours`}
          label="Payout Max Daily Overtime Hours"
          helperText={getInherited(
            inheritedSettings,
            `${taxRateTypeKey}.payout_max_daily_overtime_hours`,
          )}
        />
      </Stack>
    </>
  );
};

RateSettingsBlendedInputs.defaultProps = {
  inheritedSettings: {},
  source: 'settings',
  taxClassification: 'ten99',
  type: '8-hour-rate',
};
